// eslint-disable-next-line spaced-comment
///  <reference types="vite/client" />
import '@tunasong/ui-lib/theme/tokens.css'
import '@tunasong/ui-lib/theme/print.css'
import '@tunasong/ui-lib/primitives/primitives.css'
import { features, Provider, rootStore } from '@tunasong/redux'
import { DevBanner } from '@tunasong/tunasong-ui'
import { NetworkStatusProvider, OfflineBanner } from '@tunasong/ui-lib'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './app.js'
import { fetchLocalSession } from './auth/local-session.js'
import { SEO } from './components/seo.js'

if (import.meta.env.DEV) {
  document.documentElement.dataset.dev = 'true'
}

/**
 * Local kernel build (spec §8.1): one origin serves the SPA, `/api` and `/ws`, so both
 * endpoints are derived from where the page was loaded from rather than hardcoded — the
 * host's port is configurable (TUNA_PORT) and the SPA is built once, not per port.
 *
 * Set VITE_TUNA_LOCAL=true when building the bundle the local host serves.
 */
const configureLocalHost = async () => {
  rootStore.dispatch(features.config.actions.setHostKind({ hostKind: 'local' }))
  rootStore.dispatch(features.config.actions.setApiBaseUrl({ apiBaseUrl: '/api' }))
  rootStore.dispatch(features.config.actions.setWsBaseUrl({ wsBaseUrl: `ws://${window.location.host}/ws` }))
  /** Stored files (waveforms, chords) come from the kernel, not the cloud CDN (spec §16.3) */
  rootStore.dispatch(
    features.config.actions.setStorageBaseUrl({ storageBaseUrl: `${window.location.origin}/api/storage` })
  )
  /**
   * Spec §16.4: there is no IDP locally, but the client's guards still need a session shape.
   * Identity is data — the host says who the user is (an imported space adopts its exporter).
   */
  rootStore.dispatch(features.user.actions.setAuth(await fetchLocalSession()))
}

// Scrollbar auto-hide (progressive enhancement; styled in primitives.css).
// Opt into the hidden-until-scrolling CSS, then flag the document as
// "scrolling" for a short window after each scroll so the themed thumb only
// appears while the user is actively scrolling. Capture phase catches scrolls
// from any element (scroll events don't bubble); passive for scroll perf.
document.documentElement.dataset.scrollbarAutohide = 'true'
let scrollIdleTimer: ReturnType<typeof setTimeout> | undefined
window.addEventListener(
  'scroll',
  () => {
    document.documentElement.dataset.scrolling = 'true'
    clearTimeout(scrollIdleTimer)
    scrollIdleTimer = setTimeout(() => {
      delete document.documentElement.dataset.scrolling
    }, 900)
  },
  { capture: true, passive: true }
)

const render = () => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const root = createRoot(document.getElementById('root')!)

  root.render(
    <React.StrictMode>
      <SEO title="Tunasong" />
      <DevBanner />
      <Provider store={rootStore}>
        <NetworkStatusProvider>
          <OfflineBanner />
          <App />
        </NetworkStatusProvider>
      </Provider>
    </React.StrictMode>
  )
}

/** The session must be in the store before the first render: AuthGuard reads it synchronously */
if (import.meta.env.VITE_TUNA_LOCAL === 'true') {
  void configureLocalHost().then(render)
} else {
  render()
}
